/**
 * App Navigation Hook
 * 
 * Provides basePath-aware navigation for in-app routing.
 * This ensures that navigation works correctly regardless of whether
 * the app is mounted at '/' (app subdomain) or '/app' (main domain).
 */

import React, { createContext, useContext, useCallback, useMemo } from 'react';
import { useLocation } from 'wouter';

interface AppNavigationContextValue {
  /** The base path for the app (e.g., '/' or '/app') */
  basePath: string;
  
  /** Navigate to a path (basePath will be prepended automatically) */
  navigate: (path: string) => void;
  
  /** Current location relative to basePath */
  location: string;
}

const AppNavigationContext = createContext<AppNavigationContextValue | null>(null);

interface NavigationProviderProps {
  basePath: string;
  children: React.ReactNode;
}

/**
 * Provider that makes basePath available to all child components.
 * Wrap your app with this to enable basePath-aware navigation.
 */
export function NavigationProvider({ basePath, children }: NavigationProviderProps) {
  const [rawLocation, setLocation] = useLocation();
  
  // Normalize the basePath (remove trailing slash)
  const normalizedBasePath = basePath.endsWith('/') && basePath !== '/' 
    ? basePath.slice(0, -1) 
    : basePath;
  
  // Calculate relative location (strip basePath prefix)
  const location = useMemo(() => {
    if (normalizedBasePath === '/') {
      return rawLocation;
    }
    if (rawLocation.startsWith(normalizedBasePath)) {
      const relativePath = rawLocation.slice(normalizedBasePath.length);
      return relativePath || '/';
    }
    return rawLocation;
  }, [rawLocation, normalizedBasePath]);
  
  // Navigate function that prepends basePath
  const navigate = useCallback((path: string) => {
    console.log('navigate', path);
    // Ensure path starts with /
    const normalizedPath = path.startsWith('/') ? path : `/${path}`;
    
    // If basePath is '/', just use the path directly
    if (normalizedBasePath === '/') {
      setLocation(normalizedPath);
      return;
    }
    
    // Otherwise, prepend basePath
    const fullPath = normalizedPath === '/' 
      ? normalizedBasePath 
      : `${normalizedBasePath}${normalizedPath}`;
    
    setLocation(fullPath);
  }, [normalizedBasePath, setLocation]);
  
  const value = useMemo<AppNavigationContextValue>(() => ({
    basePath: normalizedBasePath,
    navigate,
    location,
  }), [normalizedBasePath, navigate, location]);
  
  return (
    <AppNavigationContext.Provider value={value}>
      {children}
    </AppNavigationContext.Provider>
  );
}

/**
 * Hook to access basePath-aware navigation.
 * 
 * @example
 * ```tsx
 * const { navigate, location, basePath } = useAppNavigation();
 * 
 * // Navigate to /dashboard (will become /app/dashboard if basePath is '/app')
 * navigate('/dashboard');
 * ```
 */
export function useAppNavigation(): AppNavigationContextValue {
  const context = useContext(AppNavigationContext);
  if (!context) {
    throw new Error('useAppNavigation must be used within a NavigationProvider');
  }
  return context;
}

/**
 * Hook to get just the navigate function.
 * Convenience wrapper around useAppNavigation.
 */
export function useNavigate(): (path: string) => void {
  return useAppNavigation().navigate;
}

/**
 * Hook to get the basePath.
 * Convenience wrapper around useAppNavigation.
 */
export function useBasePath(): string {
  return useAppNavigation().basePath;
}
